/**
 * Slash-menu search ranking. Hand-rolled prefix-first scorer — small N
 * (22 commands max), no Fuse.js / fzf dep needed.
 *
 * Score tiers (highest first):
 *   - slug exact match: 100
 *   - label prefix match (resolved via i18n): 80
 *   - label word-prefix match: 60
 *   - keyword prefix match (locale-aware): 50
 *   - keyword word-prefix match: 40
 *   - label substring match: 20
 *   - description substring match: 10
 *
 * Within a tier the registry's source order is preserved (stable sort).
 *
 * Search is diacritic-insensitive (NFD + strip Unicode marks) and
 * case-insensitive (`toLocaleLowerCase(locale)`). This is the standard
 * "Spanish user types 'erro' and matches 'error'" + "Spanish user types
 * 'AMARILLO' and matches 'amarillo'" behavior; same approach as Linear.
 */

import type { TFunction } from 'i18next'

import type { SlashCommand } from './slash-commands'

/** Strip accents + lowercase against the user's locale. */
function normalize(s: string, locale: string): string {
  return s
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLocaleLowerCase(locale)
}

/** Split a string into word-prefix candidates. Words are non-space runs. */
function wordPrefixes(s: string): string[] {
  // Whitespace + a small set of word boundaries (hyphen, period). Any
  // run between them is a candidate word.
  return s.split(/[\s\-./]+/).filter(Boolean)
}

interface ScoredCommand {
  command: SlashCommand
  score: number
  /** Tie-breaker — preserves registry source order within a tier. */
  index: number
}

interface ScoreInput {
  command: SlashCommand
  query: string
  locale: 'en' | 'es' | string
  t: TFunction<'wiki'>
  index: number
}

/**
 * The tier table, generalized so the toolbar's ⋯ More menu can rank its
 * entries with the SAME rules (one search feel across both pickers).
 * Returns the score, or `null` when the candidate doesn't match. An empty
 * query scores every candidate 0 (callers usually skip the scorer then).
 */
export function scoreSearchCandidate({
  slug,
  label,
  description,
  keywords,
  query,
  locale,
}: {
  slug: string
  label: string
  description?: string
  keywords: { en: readonly string[]; es: readonly string[] }
  query: string
  locale: string
}): number | null {
  const q = normalize(query, locale)
  if (q.length === 0) return 0

  const normalizedSlug = normalize(slug, locale)
  const normalizedLabel = normalize(label, locale)
  const normalizedDescription = normalize(description ?? '', locale)
  const localeKeywords =
    locale.startsWith('es') && keywords.es.length > 0 ? keywords.es : keywords.en
  const normalizedKeywords = localeKeywords.map((k) => normalize(k, locale))

  // Highest-priority signal: exact slug match. Means a power user typed
  // exactly the canonical id (e.g. `bullet-list`, `info`) and wants that
  // specific entry — surface it first regardless of label collisions.
  if (normalizedSlug === q) return 100

  // Label prefix is the next strongest signal — the human-readable name
  // starts with the query.
  if (normalizedLabel.startsWith(q)) return 80

  // Label word-prefix — "list" matches "Bullet list" because "list" is
  // a word in the label.
  if (wordPrefixes(normalizedLabel).some((w) => w.startsWith(q))) return 60

  // Keyword tier — same prefix logic on the locale's aliases set.
  if (normalizedKeywords.some((k) => k.startsWith(q))) return 50
  if (normalizedKeywords.some((k) => wordPrefixes(k).some((w) => w.startsWith(q)))) return 40

  // Substring tiers — least specific but catches mid-word matches.
  // "ull" matches "Bullet list" via label substring; "color" matches
  // "User-pickable color and icon" via description substring.
  if (normalizedLabel.includes(q)) return 20
  if (normalizedDescription.length > 0 && normalizedDescription.includes(q)) return 10

  return null
}

function scoreOne({ command, query, locale, t, index }: ScoreInput): ScoredCommand | null {
  // i18n keys are referenced with the namespace prefix `wiki:` — but
  // since the caller's `useTranslation('wiki')` already binds the
  // namespace, we pass the key sans-prefix and trust the binding. The
  // template literal makes the call site validate against the `wiki/`
  // namespace's known keys via `ParseKeys`.
  const score = scoreSearchCandidate({
    slug: command.slug,
    label: t(`slashMenu.commands.${command.i18nKey}.label`),
    description: t(`slashMenu.commands.${command.i18nKey}.description`),
    keywords: command.keywords,
    query,
    locale,
  })
  return score === null ? null : { command, score, index }
}

/**
 * Score + filter + sort a command list against a query. Pure function.
 *
 * The popover calls this on every keystroke; with N ≤ 22 and the
 * label/description i18n lookups cached internally by i18next, the work
 * per call is ~microseconds.
 */
export function searchSlashCommands({
  commands,
  query,
  locale,
  t,
}: {
  commands: SlashCommand[]
  query: string
  locale: string
  t: TFunction<'wiki'>
}): SlashCommand[] {
  if (query.trim().length === 0) {
    // Empty query — return the full list in registry order. The popover
    // groups + renders sticky category headers in this branch.
    return commands.slice()
  }

  const scored: ScoredCommand[] = []
  for (let i = 0; i < commands.length; i++) {
    const command = commands[i]
    if (!command) continue
    const result = scoreOne({ command, query, locale, t, index: i })
    if (result) scored.push(result)
  }

  // Stable sort — JS's `Array.prototype.sort` is stable on every
  // supported engine since ES2019, but we explicitly use index as the
  // tiebreaker so registry order is preserved within each score tier
  // regardless of engine.
  scored.sort((a, b) => {
    if (a.score !== b.score) return b.score - a.score
    return a.index - b.index
  })

  return scored.map((s) => s.command)
}
